import * as React from 'react';

import styled from 'styled-components';
import { getWidth } from '@components/styles';

import Flex from '@components/layout/flex';
import Button from '@components/actions/button';
import VideoIcon from '@components/basics/icons/video';
import Typography from '@components/basics/typography';

export type statusType = 'uploading' | 'done';

export interface UploadMusicVideoProps {
  className?: string;
  status?: statusType;
  imgSrc?: string;
  filename?: string;
  onChange: (files: FileList) => void;
  error?: React.ReactNode;
  inline?: boolean;
  noIcon?: boolean;
  uploaded?: number;
  total?: number;
  multiple?: boolean;
  accept: string;
  onCancel: () => void;
}

export const WrapperText = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  flex-direction: row;
  width: 100%;
  margin-bottom: 10px;

  @media ${({ theme }) => theme.device.laptopL} {
    margin-right: 20px;
    margin-bottom: 0;
    max-width: calc(100% - ${getWidth(2)} - 20px);
  }
  @media ${({ theme }) => theme.device.laptopXL} {
    max-width: calc(100% - ${getWidth(2)} - 80px);
  }
`;

const getBtnText = (type: any) => {
  switch (type) {
    case 'uploading':
      return 'Cancel';
    case 'done':
      return 'Change';
    default:
      return 'Browse';
  }
};

const getVideoText = (type: any) => {
  switch (type) {
    case 'uploading':
      return 'Uploading...';
    case 'done':
      return 'Uploaded';
    default:
      return <span>Formats: .avi, .mov</span>;
  }
};

const getTitle = (filename?: string) => {
  if (filename) {
    return filename;
  } else {
    return 'Upload Music Video';
  }
};

export const UploadMusicVideo: React.FC<UploadMusicVideoProps> = (props) => {
  const getButtonType = (type: any) => {
    if (props.error) {
      return 'primary';
    }

    if (!type) {
      return 'primary';
    }

    return 'secondary';
  };

  const handleButtonClick = () => {
    if (props.status === 'uploading') {
      props.onCancel();
    }
  };

  return (
    <Flex className={props.className} flexDirection="column">
      <div className="upload-wrapper">
        <WrapperText>
          {props.noIcon ? null : <VideoIcon className="icon" />}
          <div className="text-wrapper">
            <Typography className="title-upload" weight="700">
              {getTitle(props.filename)}
            </Typography>
            <Helper
              className="upload-music-video-helper"
              opacity=".7"
              type="small"
            >
              {getVideoText(props.status)}
            </Helper>
            {props.error ? (
              <Error className="error" weight="700">
                {props.error}
              </Error>
            ) : null}
          </div>
        </WrapperText>
        <Button
          uppercase
          className="button"
          buttonType={getButtonType(props.status)}
          type="button"
          small
          onClick={handleButtonClick}
        >
          {getBtnText(props.status)}
          {props.status !== 'uploading' ? (
            <input
              type="file"
              className="file"
              onChange={(ev) =>
                ev.target.files?.length ? props.onChange(ev.target.files) : null
              }
              multiple={!!props.multiple}
              accept={props.accept}
            />
          ) : null}
        </Button>
      </div>
    </Flex>
  );
};

export default styled(UploadMusicVideo)`
  padding: 15px;

  @media ${({ theme }) => theme.device.mobileL} {
    padding: 30px;
  }

  .album-title {
    margin-bottom: 6px;
  }

  .upload-wrapper {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: center;

    @media ${({ theme }) => theme.device.laptopL} {
      flex-direction: row;
    }
  }

  .text-wrapper {
    max-width: calc(100% - 80px);

    @media ${({ theme }) => theme.device.laptopL} {
      max-width: calc(100% - 20px);
    }

    @media ${({ theme }) => theme.device.laptopXL} {
      max-width: 100%;
    }

    .upload-music-video-helper {
      margin: 4px 0;
      @media ${({ theme }) => theme.device.laptopL} {
        margin: 4px 0 0;
      }
    }

    .title-upload {
      // word-break: break-word;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  .icon {
    margin-right: 20px;
    display: block;

    @media ${({ theme }) => theme.device.laptopL} {
      display: none;
    }

    @media ${({ theme }) => theme.device.laptopXL} {
      display: block;
    }
  }

  .button {
    position: relative;
    overflow: hidden;
    width: 100%;

    @media ${({ theme }) => theme.device.laptopL} {
      max-width: ${getWidth(2)};
    }

    .file {
      cursor: pointer;
      position: absolute;
      width: 100%;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      opacity: 0;
    }
  }
`;

const Helper = styled(Typography)`
  color: ${({ theme }) => theme.colors.primary};
`;

const Error = styled(Typography)`
  color: ${({ theme }) => theme.colors.error};
`;
